import { action, computed, observable, reaction, runInAction } from 'mobx';
import { CreditProviderProductDto, ICreditProviderProductStore, ProviderProductCreateDto } from './../types/CreditProviderProductStoreType';
import { CreditProviderEndpoints } from '../endpoints/CreditProviderEndpoints';
import { store } from 'Models/Store';
import { OrganisationProviderProductStore } from './OrganisationProviderProductStore';

const DEFAULT_PAGE_SIZE = 10;

export class CreditProviderProductStore implements ICreditProviderProductStore {
	@observable creditProviderId: string;
	@observable selectedProviderProduct: CreditProviderProductDto | undefined;
	@observable creditProviderProducts: CreditProviderProductDto[];
	@observable totalCount: number;
	@observable pageNo: number;
	@observable isLoading: boolean;
	@observable hasError: boolean;
	@observable organisationProductStore: OrganisationProviderProductStore;

	constructor() {
		this.creditProviderId = '';
		this.creditProviderProducts = [];
		this.totalCount = 0;
		this.pageNo = 1;
		this.isLoading = false;
		this.hasError = false;

		reaction(
			() => [ this.pageNo, this.creditProviderId ],
			async () => {
				await this.getAvailableCreditProviderProducts();
			}
		)
	}

	@computed get pageSize() {
		return DEFAULT_PAGE_SIZE;
	}

	@action private setOrganisationProductStore = () => {
		this.organisationProductStore = new OrganisationProviderProductStore(this.creditProviderId, this.selectedProviderProduct?.id || '');
	}

	@action setCreditProviderId = (creditProviderId: string) => {
		this.creditProviderId = creditProviderId;
	}

	@action updateProviderProduct = (product: CreditProviderProductDto) => {
		this.selectedProviderProduct = product;
	}

	@action getProviderProduct = async (providerProductId: string) => {
		this.isLoading = true;
		this.hasError = false;
		const result = await CreditProviderEndpoints.getProviderProduct(this.creditProviderId, providerProductId);
		runInAction(() => {
			if (!result) {
				this.hasError = true;
				this.isLoading = false;
				this.selectedProviderProduct = undefined;
				this.setOrganisationProductStore();
				return;
			}
			this.isLoading = false;
			this.updateProviderProduct(result);
			this.setOrganisationProductStore();
		});
	}


	@action getAvailableCreditProviderProducts = async () => {
		if (!!!this.creditProviderId) {
			return;
		}
		// Get available products for the credit provider
		this.isLoading = true;
		this.hasError = false;
		const result = await CreditProviderEndpoints.getAvailableProviderProducts(this.creditProviderId, this.pageNo, this.pageSize);
		runInAction(() => {
			if (result.totalCount === 0) {
				this.creditProviderProducts = [];
				this.totalCount = 0;
				this.hasError = true;
				this.isLoading = false;
				return;
			}

			this.creditProviderProducts = result.data;
			this.totalCount = result.totalCount;
			this.isLoading = false;
			this.setPageNo(result.pageNo);
		});
	}

	@action setPageNo = (pageNo: number) => {
		if (this.pageNo !== pageNo) {
			this.pageNo = pageNo;
		}
	}

	@action reset = () => {
		this.creditProviderId = '';
		this.creditProviderProducts = [];
		this.totalCount = 0;
		this.pageNo = 1;
		this.isLoading = false;
		this.hasError = false;
	}

	@action saveProviderProduct = async () => {
		if(!this.selectedProviderProduct) {
			return;
		}

		this.isLoading = true;
		this.hasError = false;
		const providerProduct = await CreditProviderEndpoints.saveProviderProduct(this.creditProviderId, this.selectedProviderProduct);
		runInAction(() => {
			this.isLoading = false;
			if (!!providerProduct){
				this.updateProviderProduct(providerProduct);
				// Get the available organisations for the product
				// if the product is changed to active
				if (providerProduct.isActive){
					this.organisationProductStore.getAvailableOrganisations(this.creditProviderId, providerProduct.id);
				}
				store.routerHistory.push(`/hub/settings/credit-providers/${this.creditProviderId}/products/${providerProduct.id}`);
				return;
			}
			this.selectedProviderProduct = undefined;
			this.hasError = true;
		});
	}

	@action createProviderProducts = async (providerProducts: ProviderProductCreateDto[], providerId: string): Promise<void> => {
		this.isLoading = true;
		this.hasError = false;
		const result = await CreditProviderEndpoints.createProviderProducts(providerId, providerProducts);
		runInAction(() => {
			if (!result) {
				this.hasError = true;
				this.isLoading = false;
			}
			this.isLoading = false;
			this.hasError = false;
			this.getAvailableCreditProviderProducts();
		});
	}
}